import { errorMessage } from "@atlas/shared";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Layers, X, Search, Trash2 } from "lucide-react";
import {
  fetchAdminCollections,
  createAdminCollection,
  patchAdminCollection,
  deleteAdminCollection,
  fetchAdminCollectionItems,
  addAdminCollectionItem,
  removeAdminCollectionItem,
  searchAdminItems,
  type AdminCollection,
  type AdminCollectionType,
  type AdminCollectionItemType,
} from "../../api/admin-content";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 32,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const label: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--text-secondary)",
  display: "block",
  marginBottom: 6,
};

const COLLECTION_TYPES: AdminCollectionType[] = ["peaks", "trails", "landmarks", "regions"];

const COLLECTION_TYPE_TO_ITEM_TYPE: Record<AdminCollectionType, AdminCollectionItemType> = {
  peaks: "peak",
  trails: "trail",
  landmarks: "landmark",
  regions: "region",
};

function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Item picker (search-to-add) ───────────────────────────────────────────────

function ItemPicker({ collectionId, itemType }: { collectionId: string; itemType: AdminCollectionItemType }) {
  const queryClient = useQueryClient();
  const [q, setQ] = React.useState("");
  const debouncedQ = useDebounced(q, 250);

  const searchQuery = useQuery({
    queryKey: ["admin/content/search-items", itemType, debouncedQ],
    queryFn: () => searchAdminItems(itemType, debouncedQ),
    enabled: debouncedQ.trim().length > 0,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin/content/collections", collectionId, "items"] });
    void queryClient.invalidateQueries({ queryKey: ["admin/content/collections"] });
  };

  const addMutation = useMutation({
    mutationFn: (itemId: string) => addAdminCollectionItem(collectionId, { itemType, itemId }),
    onSuccess: invalidate,
  });

  const results = searchQuery.data?.items ?? [];

  return (
    <div>
      <div style={{ position: "relative" }}>
        <Search size={13} style={{ position: "absolute", left: 9, top: 9, color: "var(--text-muted)" }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${itemType}s to add…`}
          style={{ ...inputStyle, paddingLeft: 28 }}
        />
      </div>
      {debouncedQ.trim() && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4, maxHeight: 220, overflow: "auto" }}>
          {searchQuery.isLoading ? (
            <Skel height={28} />
          ) : results.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0" }}>No matches</p>
          ) : (
            results.map((item) => (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "6px 8px",
                  borderRadius: "var(--radius-md)",
                  background: "var(--surface-sunken)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {item.name}
                  </div>
                  {item.meta && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.meta}</div>}
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={addMutation.isPending}
                  onClick={() => addMutation.mutate(item.id)}
                >
                  Add
                </Button>
              </div>
            ))
          )}
        </div>
      )}
      {addMutation.isError && (
        <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 6 }}>
          {errorMessage(addMutation.error, "Failed to add item")}
        </p>
      )}
    </div>
  );
}

// ─── Current items list ────────────────────────────────────────────────────────

function CollectionItemsSection({
  collection,
  onCountChange,
}: {
  collection: AdminCollection;
  onCountChange: (count: number) => void;
}) {
  const queryClient = useQueryClient();
  const itemsQuery = useQuery({
    queryKey: ["admin/content/collections", collection.id, "items"],
    queryFn: () => fetchAdminCollectionItems(collection.id),
  });

  // The collection object handed to this section can be stale (e.g. straight
  // after create/patch, whose responses don't include item_count) — the live
  // items list fetched here is the source of truth, so bubble its length up
  // to the drawer for the delete-confirmation logic.
  const itemsData = itemsQuery.data;
  React.useEffect(() => {
    if (itemsData) onCountChange(itemsData.items.length);
  }, [itemsData, onCountChange]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin/content/collections", collection.id, "items"] });
    void queryClient.invalidateQueries({ queryKey: ["admin/content/collections"] });
  };

  const removeMutation = useMutation({
    mutationFn: (itemId: string) => removeAdminCollectionItem(collection.id, itemId),
    onSuccess: invalidate,
  });

  const items = itemsQuery.data?.items ?? [];
  const itemType = COLLECTION_TYPE_TO_ITEM_TYPE[collection.type];

  return (
    <div>
      <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>
        Items ({items.length})
      </p>

      {itemsQuery.isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Skel height={28} /><Skel height={28} />
        </div>
      ) : items.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 10px" }}>No items yet.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", marginBottom: 10 }}>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "7px 0",
                borderBottom: "1px solid var(--border-subtle)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {item.name}
                </div>
                {item.meta && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{item.meta}</div>}
              </div>
              <button
                onClick={() => removeMutation.mutate(item.id)}
                disabled={removeMutation.isPending}
                aria-label={`Remove ${item.name}`}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ItemPicker collectionId={collection.id} itemType={itemType} />
    </div>
  );
}

// ─── Collection drawer (create + edit) ─────────────────────────────────────────

function CollectionDrawer({
  collection,
  onClose,
}: {
  collection: AdminCollection | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // Once a create succeeds we swap into edit mode in-place, so the item
  // management section becomes available without closing the drawer.
  const [active, setActive] = React.useState<AdminCollection | null>(collection);
  const isCreate = active === null;
  // POST/PATCH responses don't include item_count (only the list query
  // computes it), so track the live count from the items list itself rather
  // than trusting `active.itemCount` after a create/save.
  const [liveItemCount, setLiveItemCount] = React.useState(collection?.itemCount ?? 0);

  const [name, setName] = React.useState(collection?.name ?? "");
  const [slug, setSlug] = React.useState(collection?.slug ?? "");
  const [type, setType] = React.useState<AdminCollectionType>(collection?.type ?? "peaks");
  const [description, setDescription] = React.useState(collection?.description ?? "");
  const [region, setRegion] = React.useState(collection?.region ?? "");
  const [country, setCountry] = React.useState(collection?.country ?? "");
  const [coverImage, setCoverImage] = React.useState(collection?.coverImage ?? "");
  const [sortOrder, setSortOrder] = React.useState(String(collection?.sortOrder ?? 0));
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const invalidateList = () => void queryClient.invalidateQueries({ queryKey: ["admin/content/collections"] });

  const createMutation = useMutation({
    mutationFn: () =>
      createAdminCollection({
        slug: slug.trim() || undefined,
        name: name.trim(),
        type,
        description: description.trim() || null,
        region: region.trim() || null,
        country: country.trim() || null,
        coverImage: coverImage.trim() || null,
        sortOrder: parseInt(sortOrder, 10) || 0,
      }),
    onSuccess: ({ collection: created }) => {
      invalidateList();
      setActive(created);
    },
  });

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!active) throw new Error("No collection to update");
      return patchAdminCollection(active.id, {
        name: name.trim(),
        description: description.trim() || null,
        region: region.trim() || null,
        country: country.trim() || null,
        coverImage: coverImage.trim() || null,
        sortOrder: parseInt(sortOrder, 10) || 0,
      });
    },
    onSuccess: ({ collection: updated }) => {
      invalidateList();
      setActive(updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (force: boolean) => {
      if (!active) throw new Error("No collection to delete");
      return deleteAdminCollection(active.id, force);
    },
    onSuccess: () => {
      invalidateList();
      onClose();
    },
  });

  const busy = createMutation.isPending || patchMutation.isPending;
  const err = createMutation.error ?? patchMutation.error ?? deleteMutation.error;

  function handleDeleteClick() {
    if (!active) return;
    if (liveItemCount > 0 && !confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteMutation.mutate(liveItemCount > 0);
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.45)" }} onClick={onClose} />
      <div
        style={{
          position: "relative",
          width: 460,
          maxWidth: "92vw",
          height: "100%",
          background: "var(--surface-card)",
          borderLeft: "1px solid var(--border-strong)",
          boxShadow: "var(--shadow-xl)",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 22px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
          <div>
            <p className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>
              {isCreate ? "New collection" : "Collection"}
            </p>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>
              {active?.name ?? "Untitled"}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "16px 22px 28px", display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="Welsh 3000ers" />
            </div>
            <div>
              <label style={label}>Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as AdminCollectionType)}
                disabled={!isCreate}
                style={{ ...inputStyle, cursor: isCreate ? "pointer" : "not-allowed" }}
              >
                {COLLECTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {isCreate && (
            <div>
              <label style={label}>Slug (optional — derived from name)</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} style={inputStyle} placeholder="welsh-3000ers" />
            </div>
          )}

          <div>
            <label style={label}>Description</label>
            <textarea
              value={description ?? ""}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              style={{ ...inputStyle, height: "auto", padding: 8, resize: "vertical" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Region</label>
              <input value={region ?? ""} onChange={(e) => setRegion(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={label}>Country</label>
              <input value={country ?? ""} onChange={(e) => setCountry(e.target.value)} style={inputStyle} />
            </div>
          </div>

          <div>
            <label style={label}>Cover image URL</label>
            <input value={coverImage ?? ""} onChange={(e) => setCoverImage(e.target.value)} style={inputStyle} placeholder="https://…" />
          </div>

          <div>
            <label style={label}>Sort order</label>
            <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={{ ...inputStyle, width: 120 }} />
          </div>

          {err && (
            <p style={{ fontSize: 13, color: "var(--status-danger)", margin: 0 }}>
              {errorMessage(err, "Something went wrong")}
            </p>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              variant="secondary"
              disabled={busy || !name.trim()}
              onClick={() => (isCreate ? createMutation.mutate() : patchMutation.mutate())}
            >
              {busy ? "Saving…" : isCreate ? "Create collection" : "Save changes"}
            </Button>
          </div>

          {active && (
            <>
              <div style={{ height: 1, background: "var(--border-subtle)", margin: "4px 0" }} />
              <CollectionItemsSection collection={active} onCountChange={setLiveItemCount} />

              <div style={{ height: 1, background: "var(--border-subtle)", margin: "4px 0" }} />
              <div
                style={{
                  padding: "12px 14px",
                  border: "1px solid rgba(224, 80, 63, 0.35)",
                  borderRadius: "var(--radius-md)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>Delete collection</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                    {liveItemCount > 0
                      ? confirmDelete
                        ? `This will also delete ${liveItemCount} item link(s). Click again to confirm.`
                        : `Has ${liveItemCount} item(s) — click to confirm force-delete.`
                      : "No items — safe to delete."}
                  </div>
                </div>
                <Button variant="danger" size="sm" disabled={deleteMutation.isPending} onClick={handleDeleteClick}>
                  Delete
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AdminCollectionsPage ───────────────────────────────────────────────────────

export default function AdminCollectionsPage() {
  const { user } = useAuth();
  const [drawerCollection, setDrawerCollection] = React.useState<AdminCollection | null | "new">(null);

  const collectionsQuery = useQuery({ queryKey: ["admin/content/collections"], queryFn: fetchAdminCollections });

  if (!user?.isAdmin) return <AccessDenied />;

  const rows = collectionsQuery.data?.collections ?? [];
  const gridColumns = "minmax(0,1.6fr) 90px minmax(0,1fr) 70px 60px";
  const headerCell: React.CSSProperties = {
    fontSize: 10.5,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <AdminShell
      title="Collections"
      action={
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDrawerCollection("new")}>
          New collection
        </Button>
      }
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 10, padding: "10px 0 8px", borderBottom: "1px solid var(--border-subtle)", minWidth: 620 }}>
          <span style={headerCell}>Name</span>
          <span style={headerCell}>Type</span>
          <span style={headerCell}>Region / country</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Items</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Sort</span>
        </div>

        {collectionsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={30} /><Skel height={30} /><Skel height={30} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={<Layers size={20} />} title="No collections yet" hint="Create the first one above." style={{ margin: "12px 0" }} />
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              onClick={() => setDrawerCollection(row)}
              style={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                gap: 10,
                alignItems: "center",
                padding: "9px 0",
                borderBottom: "1px solid var(--border-subtle)",
                cursor: "pointer",
                minWidth: 620,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "var(--surface-hover)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row.name}
                </div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{row.slug}</div>
              </div>
              <span className="eyebrow" style={{ fontSize: 9.5 }}>{row.type}</span>
              <span style={{ fontSize: 12.5, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {[row.region, row.country].filter(Boolean).join(", ") || "—"}
              </span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-primary)", textAlign: "right" }}>{row.itemCount}</span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>{row.sortOrder}</span>
            </div>
          ))
        )}
      </div>

      {drawerCollection !== null && (
        <CollectionDrawer
          collection={drawerCollection === "new" ? null : drawerCollection}
          onClose={() => setDrawerCollection(null)}
        />
      )}
    </AdminShell>
  );
}
